import { SyncableRecord } from "./syncable-record";

export enum UnitType {
  Weight = "Weight",
  Volume = "Volume",
  Count = "Count",
  Other = "Other",
}

// Unit-type value → i18n key for the ingredient form's category pills (display
// only; the stored unitType is the identifier). The individual unit tokens
// (g/tsp/whole…) and fraction glyphs stay as-is — they're handled with the
// quantity formatters.
export const UNIT_TYPE_LABEL_KEYS: Record<UnitType, string> = {
  [UnitType.Weight]: "labels.unitType.weight",
  [UnitType.Volume]: "labels.unitType.volume",
  [UnitType.Count]: "labels.unitType.count",
  [UnitType.Other]: "labels.unitType.other",
};

export enum WeightUnit {
  G = "g",
  Kg = "kg",
  Oz = "oz",
  Lb = "lb",
}

export enum VolumeUnit {
  Ml = "ml",
  L = "L",
  Tsp = "tsp",
  Tbsp = "tbsp",
  Cup = "cup",
  FlOz = "fl oz",
}

export enum CountUnit {
  Whole = "whole",
  Piece = "piece",
  Pinch = "pinch",
  Handful = "handful",
  Clove = "clove",
  Slice = "slice",
  Sprig = "sprig",
}

export enum FractionUnit {
  Eighth = "⅛",
  Quarter = "¼",
  Half = "½",
  ThreeQuarters = "¾",
  Third = "⅓",
  TwoThirds = "⅔",
}

export type IngredientUnit = WeightUnit | VolumeUnit | CountUnit | string;

// The measured fields shared by an ingredient and its substitute — what the
// quantity formatters need, independent of row identity/ordering.
export interface MeasuredIngredient {
  quantity?: string | null; // a number or a range, e.g. "400", "1-2"
  fraction?: string | null;
  unitType?: UnitType | null;
  unit?: IngredientUnit | null;
  other?: string | null;
}

// An optional substitute ("1 cup butter OR 1 cup margarine"). Stored inline on
// its parent ingredient (synced as a jsonb column) rather than as its own row:
// it has no independent identity, ordering, or grouping — it lives and dies
// with the parent.
export interface IngredientSubstitute extends MeasuredIngredient {
  name: string;
}

export interface Ingredient extends SyncableRecord {
  recipeId: string;
  name: string;
  group: boolean | null;
  quantity?: string | null; // a number or a range, e.g. "400", "1-2"
  fraction?: string | null;
  unitType?: UnitType | null;
  unit?: IngredientUnit | null;
  other?: string | null;
  alternateLocale?: string | null;
  // Optional substitute ingredient. Store null when removed so the sync push
  // sends an explicit null (see the difficulty note in recipe.ts).
  substitute?: IngredientSubstitute | null;
  sortOrder: number;
}
